import { useRouterState, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import {
  CATEGORIES,
  CATEGORY_ALL,
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { IncludeEnum } from "../types";

export const INCLUDE_LABELS: Record<string, string> = {
  [CATEGORY_ALL]: "Attractions",
  [CATEGORY_ESCAPE_ROOM]: "Escape rooms",
  [CATEGORY_HAUNT]: "Haunts",
};

function includeLabelsByCategory(category: string): {
  label: string;
  labels: Record<keyof typeof IncludeEnum, string>;
} {
  const label = INCLUDE_LABELS[category];

  return {
    label,
    labels: {
      [IncludeEnum.ALL]: `All ${label.toLocaleLowerCase()}`,
      [IncludeEnum.NOT_PLAYED]: "Not yet done",
      [IncludeEnum.PLAYED]: "Already done",
      [IncludeEnum.SAVED]: "My wishlist",
    },
  };
}

export default function useSelectedCategories() {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const categoryUrl = useCallback(
    (key: string) => {
      const ALLOWED_PARAMS = [
        "coupons",
        "has_bookings",
        "include",
        "is_scary",
        "location",
        "players",
        "query",
        "sortBy",
        "status",
      ] as const;

      const newQuery: Record<string, string> = {};

      if (key !== CATEGORY_ALL) {
        newQuery.categories = key;
      }

      for (const param of ALLOWED_PARAMS) {
        const value = search[param];
        if (value) {
          newQuery[param] = String(value);
        }
      }

      return { search: newQuery, to: pathname };
    },
    [search, pathname]
  );

  const primaryCategories = useMemo(() => {
    const selectedCategories = search.categories?.split(",") ?? [];
    const selectedSubCategories = search.subcategories?.split(",") ?? [];

    const categories = Object.keys(CATEGORIES).reduce((accum, value) => {
      if (
        selectedCategories.includes(value) ||
        selectedSubCategories.some((slug) => {
          const segments = slug.split("/");
          return segments.includes(value);
        })
      ) {
        accum.push(value);
      }

      return accum;
    }, [] as string[]);

    return categories;
  }, [search.categories, search.subcategories]);

  const { label, labels } = useMemo(() => {
    if (primaryCategories.includes(CATEGORY_ESCAPE_ROOM)) {
      return includeLabelsByCategory(CATEGORY_ESCAPE_ROOM);
    }

    if (primaryCategories.includes(CATEGORY_HAUNT)) {
      return includeLabelsByCategory(CATEGORY_HAUNT);
    }

    return includeLabelsByCategory(CATEGORY_ALL);
  }, [primaryCategories]);

  return {
    categoryUrl,
    includeLabel: label,
    includeLabels: labels,
    primaryCategories,
  };
}
